import { AfterViewInit, Component, ElementRef, NgZone, OnDestroy, inject } from '@angular/core';

import { ITab } from '../../components/section-tabs/section-tabs.component';
import { ExportService, IExportBlock, IExportColumn } from '../../services/export.service';
import { FilterService, IFilterContext } from '../../services/filter.service';
import { FilterField } from '../../models/filter-option.model';
import { ISeriesPoint } from '../../models/series-point.model';
import { TableColumn } from '../../models/table-column.model';
import { FILTROS_BASE, FILTROS_DEFAULT } from '../../data/filters.data';
import {
  ESTADOS_NPS,
  HISTORICO_POR_ESTADO,
  IEstadoNpsRow,
  IMapaEstado,
  INDICADORES,
  MAPA_NPS,
} from '../../data/dashboard.data';
import { nombreEnMapa } from '../../data/dimensions.data';
import { ISucursalNps, NPS_SUCURSALES } from '../../data/home.data';
import {
  IMontoCompra,
  IRankingRegion,
  IRankingSucursal,
  MONTO_COMPRA,
  RANKING_REGION,
  RANKING_SUCURSAL,
  historicoDe,
} from '../../data/results.data';
import {
  IStoreCsatRow,
  STORE_RESULTS_COLUMNS,
  STORE_RESULTS_ROWS,
} from '../../data/store-results.data';

/**
 * Explorar — «¿Dónde está el problema?».
 *
 * Reúne la geografía (coropleta y detalle por estado), el detalle por sucursal
 * (NPS, ranking de nueve columnas y CSAT por tienda), el ranking por región y
 * el detalle por monto de compra. Antes eran tres pantallas sueltas —Dashboard,
 * Resultados Generales y Resultados por tienda—; ahora son las cuatro pestañas
 * de una misma pregunta.
 */
@Component({
  selector: 'app-explore-page',
  standalone: false,
  templateUrl: './explore-page.component.html',
  styleUrl: './explore-page.component.scss',
})
export class ExplorePageComponent implements AfterViewInit, OnDestroy {

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR NAVEGACION                             */
  /* -------------------------------------------------------------------------- */

  public readonly tabs: Array<ITab> = [
    { id: 'mapa',       label: 'Mapa',       icon: 'bi-geo-alt' },
    { id: 'sucursales', label: 'Sucursales', icon: 'bi-shop' },
    { id: 'regiones',   label: 'Regiones',   icon: 'bi-diagram-3' },
    { id: 'ticket',     label: 'Ticket',     icon: 'bi-receipt' },
  ];

  /** Sin tipar como union: `activeChange` de las pestañas emite `string`. */
  public tab: string = 'mapa';

  public readonly filtros: Array<FilterField> = FILTROS_BASE;

  public seleccion: Record<string, Array<string>> = { ...FILTROS_DEFAULT };

  private readonly filterService: FilterService = inject(FilterService);
  private readonly exportService: ExportService = inject(ExportService);

  private ctx: IFilterContext = this.filterService.contexto(this.seleccion);

  /** Instancia de ECharts del mapa; la necesita el botón IMAGEN. */
  private mapaInstancia?: { getDataURL(opciones?: any): string };

  /* -------------------------------------------------------------------------- */
  /*                                 ANCHOR DATOS                               */
  /* -------------------------------------------------------------------------- */

  public readonly indicadores: Array<string> = INDICADORES;

  public mapa: Array<IMapaEstado> = MAPA_NPS;
  public estados: Array<IEstadoNpsRow> = ESTADOS_NPS;
  public sucursales: Array<ISucursalNps> = NPS_SUCURSALES;
  public rankingSucursal: Array<IRankingSucursal> = RANKING_SUCURSAL;
  public csatTiendas: Array<IStoreCsatRow> = STORE_RESULTS_ROWS;
  public rankingRegion: Array<IRankingRegion> = RANKING_REGION;
  public montoCompra: Array<IMontoCompra> = MONTO_COMPRA;

  /** Qué está acotando la vista; vacío cuando no hay filtros de universo. */
  public alcance: string = '';

  public indicador: string = 'NPS';

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR COLUMNAS                             */
  /* -------------------------------------------------------------------------- */

  public readonly columnasEstado: Array<TableColumn> = [
    { key: 'estado',      title: 'ESTADO',      headerStyle: 'default',   cellStyle: 'text' },
    { key: 'detractores', title: 'DETRACTORES', headerStyle: 'detractor', cellStyle: 'center', suffix: ' %' },
    { key: 'pasivos',     title: 'PASIVOS',     headerStyle: 'pasivo',    cellStyle: 'center', suffix: ' %' },
    { key: 'promotores',  title: 'PROMOTORES',  headerStyle: 'promotor',  cellStyle: 'center', suffix: ' %' },
    { key: 'nps',         title: 'NPS',         headerStyle: 'default',   cellStyle: 'nps',    suffix: ' %' },
    { key: 'historico',   title: 'HISTÓRICO',   headerStyle: 'default',   cellStyle: 'icon',
      icon: 'bi-graph-up', sortable: false },
  ];

  public readonly columnasSucursalNps: Array<TableColumn> = [
    { key: 'sucursal', title: 'SUCURSAL', cellStyle: 'text' },
    { key: 'nps',      title: 'NPS',      cellStyle: 'nps', suffix: ' %', width: '110px' },
  ];

  public readonly columnasRanking: Array<TableColumn> = [
    { key: 'clave',           title: 'CLAVE',    width: '90px' },
    { key: 'sucursal',        title: 'SUCURSAL', width: '260px' },
    { key: 'detractores',     title: 'DETRACTORES',           headerStyle: 'detractor', cellStyle: 'center' },
    { key: 'pasivos',         title: 'PASIVOS',               headerStyle: 'pasivo',    cellStyle: 'center' },
    { key: 'promotores',      title: 'PROMOTORES',            headerStyle: 'promotor',  cellStyle: 'center' },
    { key: 'neto',            title: 'NETO',                  cellStyle: 'nps' },
    { key: 'netoComerciante', title: 'NETO COMERCIANTE',      cellStyle: 'nps' },
    { key: 'netoConsumidor',  title: 'NETO CONSUMIDOR FINAL', cellStyle: 'nps' },
    { key: 'historico',       title: 'HISTÓRICO', cellStyle: 'icon', icon: 'bi-bar-chart-line-fill', sortable: false },
  ];

  public readonly columnasCsat: Array<TableColumn> = STORE_RESULTS_COLUMNS;

  public readonly columnasRegion: Array<TableColumn> = [
    { key: 'variable',    title: 'VARIABLE' },
    { key: 'detractores', title: 'DETRACTORES', headerStyle: 'detractor', cellStyle: 'center' },
    { key: 'pasivos',     title: 'PASIVOS',     headerStyle: 'pasivo',    cellStyle: 'center' },
    { key: 'promotores',  title: 'PROMOTORES',  headerStyle: 'promotor',  cellStyle: 'center' },
    { key: 'nps',         title: 'NPS',         cellStyle: 'nps' },
    { key: 'historico',   title: 'HISTÓRICO',   cellStyle: 'icon', icon: 'bi-bar-chart-line-fill', sortable: false },
  ];

  public readonly columnasMonto: Array<TableColumn> = [
    { key: 'rango',       title: 'RANGO' },
    { key: 'porcentaje',  title: '%', cellStyle: 'center', suffix: '%' },
    { key: 'detractores', title: 'DETRACTORES', headerStyle: 'detractor', cellStyle: 'center' },
    { key: 'pasivos',     title: 'PASIVOS',     headerStyle: 'pasivo',    cellStyle: 'center' },
    { key: 'promotores',  title: 'PROMOTORES',  headerStyle: 'promotor',  cellStyle: 'center' },
    { key: 'neto',        title: 'NETO',        cellStyle: 'nps' },
    { key: 'historico',   title: 'HISTÓRICO',   cellStyle: 'icon', icon: 'bi-bar-chart-line-fill', sortable: false },
  ];

  public readonly exportacion: Array<string> = ['Copiar', 'CSV', 'Excel', 'PDF'];
  public readonly exportacionPrint: Array<string> = ['Copiar', 'CSV', 'Excel', 'PDF', 'Print'];
  public readonly exportacionMapa: Array<string> = ['Copiar', 'CSV', 'PDF', 'Print'];

  /* -------------------------------------------------------------------------- */
  /*                                 ANCHOR MODAL                               */
  /* -------------------------------------------------------------------------- */

  public historialAbierto: boolean = false;
  public historialTitulo: string = '';
  public historialPuntos: Array<ISeriesPoint> = [];

  /** Texto con el que la fuente publica una tienda sin muestra suficiente. */
  private static readonly SIN_DATO: string = 'N/D';

  private observador?: MutationObserver;

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private zone: NgZone,
  ) {
    this.aplicar();
  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR ACCIONES                             */
  /* -------------------------------------------------------------------------- */

  public onIndicador(valor: string): void {
    this.indicador = valor;
  }

  public onFiltros(valores: Record<string, Array<string>>): void {
    this.seleccion = valores;
    this.ctx = this.filterService.contexto(valores);
    this.cerrarHistorico();
    this.aplicar();
  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR EXPORTACION                             */
  /* -------------------------------------------------------------------------- */

  /** Contexto que cada `<app-data-table>` escribe en la cabecera del archivo. */
  public get filtrosTexto(): string {
    return this.filterService.descripcion(this.seleccion, this.filtros);
  }

  /** Título de la pestaña activa: es lo que se está viendo. */
  private get tituloPestana(): string {
    return this.tabs.find((pestana) => pestana.id === this.tab)?.label ?? 'Explorar';
  }

  /**
   * «Descargar CSV» baja la pestaña abierta, no la sección entera: en
   * Sucursales son tres tablas de 145 filas y en Ticket son cinco renglones.
   * Bajar las cuatro pestañas juntas daría un archivo que nadie pidió.
   */
  public descargar(): void {
    this.exportService.csv(
      this.bloques(),
      this.exportService.nombreArchivo('nps', 'explorar', this.tituloPestana),
      {
        titulo: `Explorar · ${this.tituloPestana}`,
        filtros: this.filtrosTexto,
      },
    );
  }

  private bloques(): Array<IExportBlock> {
    const bloque = (titulo: string, columnas: Array<any>, filas: Array<any>): IExportBlock => ({
      titulo,
      columnas: this.columnasExportables(columnas),
      filas,
    });

    switch (this.tab) {
      case 'sucursales':
        return [
          bloque('NPS por sucursal', this.columnasSucursalNps, this.sucursales),
          bloque('Ranking por sucursal', this.columnasRanking, this.rankingSucursal),
          bloque('CSAT por tienda', this.columnasCsat, this.csatTiendas),
        ];

      case 'regiones':
        return [bloque('Ranking por región, zona y sucursal', this.columnasRegion, this.rankingRegion)];

      case 'ticket':
        return [bloque('Detalle por monto de compra', this.columnasMonto, this.montoCompra)];

      default:
        return [bloque('NPS · detalle por estado', this.columnasEstado, this.estados)];
    }
  }

  /** Las columnas de icono son controles: en un archivo sólo dejan un hueco. */
  private columnasExportables(columnas: Array<TableColumn>): Array<IExportColumn> {
    return columnas
      .filter((columna) => columna.cellStyle !== 'icon')
      .map((columna) => ({ key: columna.key, title: columna.title, suffix: columna.suffix }));
  }

  /* ------------------------------ mapa ------------------------------------- */

  /** ngx-echarts entrega la instancia; sin ella no hay PNG que descargar. */
  public onMapaListo(instancia: any): void {
    this.mapaInstancia = instancia;
  }

  /**
   * PNG del lienzo, que ECharts sabe entregar solo.
   *
   * El fondo va en blanco a propósito: el lienzo es transparente y un PNG
   * transparente pegado en una lámina clara deja los estados invisibles.
   */
  public descargarMapaImagen(): void {
    const dataUrl = this.mapaInstancia?.getDataURL({
      backgroundColor: '#ffffff',
      pixelRatio: 2,
      type: 'png',
    });

    if (dataUrl) {
      this.exportService.png(dataUrl, this.exportService.nombreArchivo('nps', 'mapa'));
    }
  }

  /** El mapa se imprime junto con la tabla que lo acompaña. */
  public imprimirMapa(): void {
    this.exportService.imprimir(this.bloques(), {
      titulo: 'Explorar · NPS por estado',
      filtros: this.filtrosTexto,
    });
  }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR RECÁLCULO                             */
  /* -------------------------------------------------------------------------- */

  /**
   * Casi todo lo de esta sección tiene la dimensión encima y se filtra de
   * verdad: el detalle por estado y el mapa por `estado`, las tres tablas de
   * sucursal cruzando con el catálogo de dimensiones, y el ranking por región
   * contra los nombres elegidos de región, zona y sucursal.
   *
   * El único bloque sin ninguna dimensión es el detalle por monto de compra
   * —el original lo publica como un agregado suelto—, así que ahí las cifras se
   * derivan.
   */
  private aplicar(): void {
    const ctx = this.ctx;

    this.alcance = this.filterService.resumenAlcance(ctx);

    this.estados = this.filterService
      .filas(ESTADOS_NPS, this.seleccion, ['estado'])
      .map((fila) => this.derivarEstado(fila));

    this.mapa = this.mapaDe(this.estados);

    this.sucursales = this.filterService.porSucursal(NPS_SUCURSALES, ctx, 'sucursal')
      .map((fila) => ({
        ...fila,
        nps: this.filterService.desviar(fila.nps, ctx, `sucursal:${fila.sucursal}`, 5, 10, 99),
      }));

    this.rankingSucursal = this.filterService.porSucursal(RANKING_SUCURSAL, ctx, 'sucursal')
      .map((fila) => this.derivarRanking(fila));

    this.csatTiendas = this.filterService.porSucursal(STORE_RESULTS_ROWS, ctx, 'sucursal')
      .map((fila) => this.derivarCsat(fila));

    this.rankingRegion = this.filterService.porNombre(
      RANKING_REGION, this.seleccion, 'variable', ['region', 'zona', 'sucursal'],
    ).map((fila) => this.derivarRegion(fila));

    // El reparto por monto tiene que seguir sumando 100: se reparte el total en
    // bloque en vez de mover cada renglón por su cuenta.
    const pesos = this.filterService.reparto(
      MONTO_COMPRA.map((fila) => Math.round(fila.porcentaje)), ctx, 'monto');

    this.montoCompra = MONTO_COMPRA.map((fila, i) => this.derivarMonto(fila, pesos[i]));
  }

  /**
   * La coropleta se arma desde la tabla ya filtrada y derivada, traduciendo el
   * nombre del programa al del GeoJSON: así el mapa y la tabla nunca dicen
   * cosas distintas del mismo estado.
   */
  private mapaDe(filas: Array<IEstadoNpsRow>): Array<IMapaEstado> {
    return filas.map((fila) => ({ name: nombreEnMapa(fila.estado), value: fila.nps }));
  }

  private derivarEstado(fila: IEstadoNpsRow): IEstadoNpsRow {
    const ctx = this.ctx;
    const detractores = this.filterService.desviar(fila.detractores, ctx, `edo:${fila.estado}:d`, 3, 1, 30);
    const pasivos = this.filterService.desviar(fila.pasivos, ctx, `edo:${fila.estado}:p`, 4, 5, 40);
    const promotores = 100 - detractores - pasivos;

    return { ...fila, detractores, pasivos, promotores, nps: promotores - detractores };
  }

  private derivarRanking(fila: IRankingSucursal): IRankingSucursal {
    const ctx = this.ctx;
    const detractores = this.filterService.desviar(fila.detractores, ctx, `rk:${fila.clave}:d`, 3, 1, 30);
    const pasivos = this.filterService.desviar(fila.pasivos, ctx, `rk:${fila.clave}:p`, 4, 5, 40);
    const promotores = 100 - detractores - pasivos;
    const neto = promotores - detractores;

    return {
      ...fila,
      detractores,
      pasivos,
      promotores,
      neto,
      netoComerciante: this.filterService.desviar(neto - 3, ctx, `rk:${fila.clave}:c`, 3, 20, 95),
      netoConsumidor: this.filterService.desviar(neto + 2, ctx, `rk:${fila.clave}:f`, 3, 20, 95),
    };
  }

  /** El CSAT por tienda viene como texto («69.44%», «N/D»): se respeta el N/D. */
  private derivarCsat(fila: IStoreCsatRow): IStoreCsatRow {
    const ctx = this.ctx;
    const mover = (valor: string, sufijo: string): string => {
      if (valor === ExplorePageComponent.SIN_DATO) {
        return valor;
      }

      const numero = Number.parseFloat(valor);

      if (Number.isNaN(numero)) {
        return valor;
      }

      return `${this.filterService
        .desviarDecimal(numero, ctx, `csat:${fila.clave}:${sufijo}`, 6, 5, 100)
        .toFixed(2)}%`;
    };

    return {
      ...fila,
      csatPrecios: mover(fila.csatPrecios, 'precios'),
      csatChiles: mover(fila.csatChiles, 'chiles'),
      csatHuevo: mover(fila.csatHuevo, 'huevo'),
      csatFrutas: mover(fila.csatFrutas, 'frutas'),
      csatCremeria: mover(fila.csatCremeria, 'cremeria'),
    };
  }

  private derivarRegion(fila: IRankingRegion): IRankingRegion {
    const ctx = this.ctx;
    const detractores = this.filterService.desviar(fila.detractores, ctx, `rg:${fila.variable}:d`, 3, 1, 30);
    const pasivos = this.filterService.desviar(fila.pasivos, ctx, `rg:${fila.variable}:p`, 4, 5, 40);
    const promotores = 100 - detractores - pasivos;

    return { ...fila, detractores, pasivos, promotores, nps: promotores - detractores };
  }

  private derivarMonto(fila: IMontoCompra, peso: number): IMontoCompra {
    const ctx = this.ctx;
    const detractores = this.filterService.desviar(fila.detractores, ctx, `mt:${fila.rango}:d`, 3, 1, 30);
    const pasivos = this.filterService.desviar(fila.pasivos, ctx, `mt:${fila.rango}:p`, 4, 5, 40);
    const promotores = 100 - detractores - pasivos;

    return {
      ...fila,
      porcentaje: this.filterService.deriva(ctx) ? peso : fila.porcentaje,
      detractores,
      pasivos,
      promotores,
      neto: promotores - detractores,
    };
  }

  /** Columna HISTÓRICO del detalle por estado: la serie viene tabulada. */
  public abrirHistoricoEstado(fila: IEstadoNpsRow): void {
    this.historialTitulo = fila.estado;
    this.historialPuntos = HISTORICO_POR_ESTADO[fila.estado] ?? [];
    this.historialAbierto = true;
  }

  /** Columna HISTÓRICO del resto de las tablas: la serie se deriva del valor. */
  public abrirHistorico(titulo: string, valor: number): void {
    this.historialTitulo = titulo;
    this.historialPuntos = historicoDe(valor);
    this.historialAbierto = true;
  }

  public cerrarHistorico(): void {
    this.historialAbierto = false;
  }

  /* -------------------------------------------------------------------------- */
  /*                           ANCHOR CELDAS SIN DATO                           */
  /* -------------------------------------------------------------------------- */

  public ngAfterViewInit(): void {
    this.marcarCeldasSinDato();

    if (typeof MutationObserver !== 'function') {
      return;
    }

    // La tabla es un componente compartido y no tiñe por valor de celda, así
    // que el «N/D» se marca sobre el DOM ya pintado: cambiar de pestaña,
    // paginar, ordenar o buscar vuelve a dispararlo. Es puro acabado —no toca
    // datos ni estado—, por eso corre fuera de la zona y no provoca detección
    // de cambios.
    this.zone.runOutsideAngular(() => {
      this.observador = new MutationObserver(() => this.marcarCeldasSinDato());
      this.observador.observe(this.elementRef.nativeElement, {
        characterData: true,
        childList: true,
        subtree: true,
      });
    });
  }

  public ngOnDestroy(): void {
    this.observador?.disconnect();
  }

  /** Atenúa por CSS las celdas de CSAT cuyo valor publicado es «N/D». */
  private marcarCeldasSinDato(): void {
    const celdas = this.elementRef.nativeElement
      .querySelectorAll<HTMLElement>('.exp-csat td.z-cell-center');

    celdas.forEach((celda) => {
      const sinDato = celda.textContent?.trim() === ExplorePageComponent.SIN_DATO;
      celda.classList.toggle('exp-sin-dato', sinDato);
    });
  }
}
